import React, { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useGetCmsConfigsQuery } from '../../apis/cmsApi/cms.hooks.api';
import { useUpdateOrderMutation } from '../../apis/mlkApi/mlk.api.hooks';
import { Button } from '../../components/Button/Button';
import { ConciergeForm } from '../../components/ConciergeForm/ConciergeForm';
import Layout, { OrderRequestResult } from '../../components/Layout/Layout';
import { SeparatorLine } from '../../components/SeparatorLine/SeparatorLine';
import { useBack } from '../../hooks/useBack';
import { OpeningHoursForm, useOpeningHoursForm } from '../../hooks/validations';
import { useLanguage } from '../../locale/useLanguage';
import {
  FaqFeature,
  OperationType,
  //  PremiseType
} from '../../models';
import { Routes } from '../../routes/types';
import { parseRes } from '../../apis/utils';
import { buildReceptionDays } from '../CreateOrEditAddress/createOrEditAddressHelper';
import { extractOpeningHoursFromOrder } from './openingHoursHelper';

import './OpeningHoursView.css';
import { StrongRegistrationWrap } from '../../components/StrongRegistrationWrap/StrongRegistrationWrap';
import { useDispatch } from 'react-redux';
import { setOperation } from '../../redux/general/general.actions';

type OpeningHoursViewProps = {};

export const OpeningHoursView: React.FC<OpeningHoursViewProps> = () => {
  const [t] = useLanguage();
  const back = useBack();
  const history = useHistory();
  const [updateOrderApiCall] = useUpdateOrderMutation();
  const dispatch = useDispatch();
  const [openingHours, setOpeningHours] = useState<OpeningHoursForm>();
  const [orderId, setOrderId] = useState<number>();
  const { data: cmsConfigurations } = useGetCmsConfigsQuery(undefined);

  const {
    // premiseTypeField,
    // onPremiseTypeChange,
    weekDaysField,
    onWeekDaysChange,
    onReceptionAfternoonFromHourChange,
    onReceptionAfternoonToHourChange,
    onReceptionMorningFromHourChange,
    onReceptionMorningToHourChange,
    receptionAfternoonFromHourField,
    receptionAfternoonToHourField,
    receptionMorningFromHourField,
    receptionMorningToHourField,
    handleSubmit,
    errors,
  } = useOpeningHoursForm(cmsConfigurations, openingHours);

  const setupExistingOpeningHours = useCallback(
    (orderResult: OrderRequestResult) => {
      setOrderId(orderResult.data?.id);
      const openingHours = extractOpeningHoursFromOrder(orderResult);
      if (openingHours) {
        setOpeningHours({
          conciergeAfternoonFromHour:
            openingHours!.afternoon!.from.split(':')[0],
          conciergeAfternoonToHour: openingHours!.afternoon!.to.split(':')[0],
          conciergeMorningFromHour: openingHours!.morning!.from.split(':')[0],
          conciergeMorningToHour: openingHours!.morning!.to.split(':')[0],
          days: openingHours.days,
          // premiseType: PremiseType.Reception,
        });
      }
    },
    []
  );

  const updateOrder = useMemo(() => {
    return handleSubmit((openingHours: OpeningHoursForm) => {
      const reception = buildReceptionDays({
        receptionMorningFromHour: openingHours.conciergeMorningFromHour,
        receptionMorningToHour: openingHours.conciergeMorningToHour,
        receptionAfternoonFromHour: openingHours.conciergeAfternoonFromHour,
        receptionAfternoonToHour: openingHours.conciergeAfternoonToHour,
        days: openingHours.days,
      });

      updateOrderApiCall({
        reception,
        orderId: `${orderId}`,
      }).then((res) => {
        const { successRes, errorRes } = parseRes(res);
        if (successRes && !errorRes.error) {
          history.replace(Routes.ConfirmationOpeningHours, {
            openingHours,
          });
        }
        if (errorRes) {
          // TODO: Handle error
        }
      });
    });
  }, [handleSubmit, history, orderId, updateOrderApiCall]);

  const daysError = errors.days as any;

  const goToInfo = useCallback(() => {
    history.push(Routes.Faq.replace(':feature', FaqFeature.Reception));
  }, [history]);

  const backAndRemoveOperation = useCallback(() => {
    dispatch(setOperation(null));
    back();
  }, [back, dispatch]);

  return (
    <Layout
      hasBack
      hasInfo
      onInfoClick={goToInfo}
      onBackClick={backAndRemoveOperation}
      onOrderChange={setupExistingOpeningHours}
      title={t('pageTitles.openingHours')}
    >
      <h2>{t('openingHours.title')}</h2>
      <p className="opening-hours--description">
        {t('openingHours.description')}
      </p>
      <a role="link" onClick={goToInfo} className="link opening-hours--link">
        {t('openingHours.howDoesItWork')}
      </a>
      <SeparatorLine />
      <div className="opening-hours--concierge-form-container">
        <ConciergeForm
          selectedWeekDays={weekDaysField.value}
          onWeekDaysChange={onWeekDaysChange}
          conciergeMorningFromHour={receptionMorningFromHourField.value}
          conciergeMorningFromHourChange={onReceptionMorningFromHourChange}
          conciergeMorningToHour={receptionMorningToHourField.value}
          conciergeMorningToHourChange={onReceptionMorningToHourChange}
          conciergeAfternoonFromHour={receptionAfternoonFromHourField.value}
          conciergeAfternoonFromHourChange={onReceptionAfternoonFromHourChange}
          conciergeAfternoonToHour={receptionAfternoonToHourField.value}
          conciergeAfternoonToHourChange={onReceptionAfternoonToHourChange}
          daysFieldError={daysError?.message}
          // onPremiseTypeChange={onPremiseTypeChange}
          // premiseTypeFieldValue={premiseTypeField.value}
          conciergeOpeningHoursError={
            errors.conciergeMorningFromHour?.message ||
            errors.conciergeMorningToHour?.message ||
            errors.conciergeAfternoonFromHour?.message ||
            errors.conciergeAfternoonToHour?.message
          }
        />
      </div>
      <div className="opening-hours--buttons-container">
        <StrongRegistrationWrap
          successAction={updateOrder}
          operationType={OperationType.OpeningHours}
        >
          <Button isPrimary label={t('openingHours.confirm')} />
        </StrongRegistrationWrap>
        <div className="opening-hours--button-spacer">
          <Button label={t('openingHours.cancel')} />
        </div>
      </div>
    </Layout>
  );
};
